import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

export interface TaxRateHistory {
  id?: number;
  hsnId: number;
  hsnCode: string;
  igstRate: number;
  cgstRate: number;
  sgstRate: number;
  cessRate: number;
  effectiveFrom: string; // ISO date string
  effectiveTo?: string; // ISO date string (optional)
  reasonForChange?: string;
  changedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TaxRateHistoryCreateRequest {
  hsn: {
    id: number;
    hsnCode: string;
  };
  igstRate: number;
  cgstRate: number;
  sgstRate: number;
  cessRate: number;
  effectiveFrom: string; // ISO date string
  reasonForChange?: string;
  changedBy?: string;
}

export interface ApplicableTaxRate {
  igstRate: number;
  cgstRate: number;
  sgstRate: number;
  cessRate: number;
  effectiveFrom: string;
}


class TaxRateHistoryApiService extends BaseApiService {
  // Get tax rate history by HSN ID
  async getTaxRateHistoryByHsnId(hsnId: number): Promise<TaxRateHistory[]> {
    try {
      const response = await this.makeRequest<ApiResponse<TaxRateHistory[]>>(
        API_ENDPOINTS.TAX_RATE_HISTORY.BY_HSN_ID(hsnId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch tax rate history');
    } catch (error) {
      // console.error('Error fetching tax rate history:', error);
      toast.error('Failed to fetch tax rate history');
      throw error;
    }
  }

  // Create new tax rate history entry
  async createTaxRateHistory(data: TaxRateHistoryCreateRequest): Promise<TaxRateHistory> {
    try {
      const response = await this.makeRequest<ApiResponse<TaxRateHistory>>(
        API_ENDPOINTS.TAX_RATE_HISTORY.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(data),
        }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to create tax rate history');
    } catch (error) {
      // console.error('Error creating tax rate history:', error);
      toast.error('Failed to create tax rate history');
      throw error;
    }
  }

  // Get applicable tax rate for HSN on a specific date
  async getApplicableTaxRate(hsnId: number, date: string): Promise<ApplicableTaxRate> {
    try {
      const response = await this.makeRequest<ApiResponse<ApplicableTaxRate>>(
        API_ENDPOINTS.TAX_RATE_HISTORY.APPLICABLE_RATE(hsnId, date),
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch applicable tax rate');
    } catch (error) {
      // console.error('Error fetching applicable tax rate:', error);
      toast.error('Failed to fetch applicable tax rate');
      throw error;
    }
  }
}

export const taxRateHistoryApiService = new TaxRateHistoryApiService();

